import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";

export const FACE_LANDMARKER_ERROR_CODES = Object.freeze({
  UNSUPPORTED_BROWSER: "UNSUPPORTED_BROWSER",
  ASSET_LOAD_FAILED: "ASSET_LOAD_FAILED",
  INIT_TIMEOUT: "INIT_TIMEOUT",
  INIT_FAILED: "INIT_FAILED",
  RUNTIME_FAILED: "RUNTIME_FAILED",
  CANCELLED: "CANCELLED",
});

const DEFAULT_RUNTIME_RECOVERY_TIMEOUT_MS = 20000;
const TIMESTAMP_INCREMENT_MS = 0.001;

export class FaceLandmarkerError extends Error {
  constructor(code, message, details = {}) {
    super(message);
    this.name = "FaceLandmarkerError";
    this.code = code;
    this.phase = details.phase || null;
    this.delegate = details.delegate || null;
    this.originalError = details.originalError || null;
  }
}

function errorMessage(error) {
  if (!error) return "";
  if (typeof error === "string") return error;
  return error.message || String(error);
}

export function classifyFaceLandmarkerError(error, details = {}) {
  if (error instanceof FaceLandmarkerError) {
    if (!error.phase && details.phase) error.phase = details.phase;
    if (!error.delegate && details.delegate) error.delegate = details.delegate;
    return error;
  }

  const message = errorMessage(error);
  const lowerMessage = message.toLowerCase();
  let code = FACE_LANDMARKER_ERROR_CODES.INIT_FAILED;

  if (details.phase === "runtime") {
    code = FACE_LANDMARKER_ERROR_CODES.RUNTIME_FAILED;
  } else if (details.phase === "assets") {
    // Safari often reports asset/network failures as only "Load failed".
    // The phase is more reliable than browser-specific message text here.
    code = FACE_LANDMARKER_ERROR_CODES.ASSET_LOAD_FAILED;
  } else if (
    lowerMessage.includes("webassembly is not defined") ||
    lowerMessage.includes("wasm is not defined")
  ) {
    code = FACE_LANDMARKER_ERROR_CODES.UNSUPPORTED_BROWSER;
  } else if (
    lowerMessage.includes("failed to fetch") ||
    lowerMessage.includes("network") ||
    lowerMessage.includes("404") ||
    lowerMessage.includes("model") ||
    lowerMessage.includes("wasm") ||
    lowerMessage.includes("compile")
  ) {
    code = FACE_LANDMARKER_ERROR_CODES.ASSET_LOAD_FAILED;
  }

  return new FaceLandmarkerError(
    code,
    message || "FaceLandmarker operation failed",
    {
      phase: details.phase || "initialization",
      delegate: details.delegate,
      originalError: error,
    }
  );
}

function withTimeout(promise, timeoutMs, details) {
  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) {
    return Promise.reject(
      new FaceLandmarkerError(
        FACE_LANDMARKER_ERROR_CODES.INIT_TIMEOUT,
        "FaceLandmarker initialization timed out",
        details
      )
    );
  }

  let timeoutId = null;
  const timeoutPromise = new Promise((resolve, reject) => {
    timeoutId = setTimeout(() => {
      reject(
        new FaceLandmarkerError(
          FACE_LANDMARKER_ERROR_CODES.INIT_TIMEOUT,
          details.phase === "runtime"
            ? "FaceLandmarker CPU recovery timed out"
            : "FaceLandmarker initialization timed out",
          details
        )
      );
    }, timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutId !== null) clearTimeout(timeoutId);
  });
}

function safeClose(instance) {
  if (!instance || typeof instance.close !== "function") return;
  try {
    instance.close();
  } catch (error) {
    console.warn("Failed to close FaceLandmarker instance", error);
  }
}

function resolveMediaPipeAssetBase(customBaseUrl) {
  const documentBase =
    typeof document !== "undefined" && document.baseURI
      ? document.baseURI
      : "http://localhost/";
  const configuredBase =
    customBaseUrl ||
    `${process.env.BASE_URL || "/"}mediapipe/`;
  const url = new URL(configuredBase, documentBase);
  return url.href.endsWith("/") ? url.href : `${url.href}/`;
}

export class ModernFaceMesh {
  constructor(config = {}) {
    this.faceLandmarker = null;
    this.visionFileset = null;
    this.isInitialized = false;
    this.onResultsCallback = null;
    this.resultsCallbackGeneration = 0;
    this.delegate = null;
    this.options = {
      maxNumFaces: 1,
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5,
    };

    this.assetBaseUrl = resolveMediaPipeAssetBase(config.assetBaseUrl);
    this.fetchImpl = Object.prototype.hasOwnProperty.call(config, "fetchImpl")
      ? config.fetchImpl
      : typeof fetch === "function"
        ? (...args) => fetch(...args)
        : null;
    this.modelAssetUrl = null;
    this.modelAssetBufferPromise = null;
    this.onDelegateChange = config.onDelegateChange || null;
    this.onRuntimeError = config.onRuntimeError || null;
    this.onRuntimeRecoveryState = config.onRuntimeRecoveryState || null;
    this.runtimeRecoveryTimeoutMs =
      config.runtimeRecoveryTimeoutMs || DEFAULT_RUNTIME_RECOVERY_TIMEOUT_MS;

    this.lastTimestamp = -Infinity;
    this.initializationGeneration = 0;
    this.runtimeRecoveryAttempted = false;
    this.runtimeRecoveryPromise = null;
    this.runtimeCloseScheduled = false;
    this.isClosed = false;

    // 与旧版 FaceMesh 接口兼容的连接常量。
    this.FACEMESH_LIPS = [[61,146],[146,91],[91,181],[181,84],[84,17],[17,314],[314,405],[405,321],[321,375],[375,291],[61,185],[185,40],[40,39],[39,37],[37,0],[0,267],[267,269],[269,270],[270,409],[409,291],[78,95],[95,88],[88,178],[178,87],[87,14],[14,317],[317,402],[402,318],[318,324],[324,308],[78,191],[191,80],[80,81],[81,82],[82,13],[13,312],[312,311],[311,310],[310,415],[415,308]];
    this.FACEMESH_LEFT_EYE = [[263,249],[249,390],[390,373],[373,374],[374,380],[380,381],[381,382],[382,362],[263,466],[466,388],[388,387],[387,386],[386,385],[385,384],[384,398],[398,362]];
    this.FACEMESH_RIGHT_EYE = [[33,7],[7,163],[163,144],[144,145],[145,153],[153,154],[154,155],[155,133],[33,246],[246,161],[161,160],[160,159],[159,158],[158,157],[157,173],[173,133]];
    this.FACEMESH_FACE_OVAL = [[10,338],[338,297],[297,332],[332,284],[284,251],[251,389],[389,356],[356,454],[454,323],[323,361],[361,288],[288,397],[397,365],[365,379],[379,378],[378,400],[400,377],[377,152],[152,148],[148,176],[176,149],[149,150],[150,136],[136,172],[172,58],[58,132],[132,93],[93,234],[234,127],[127,162],[162,21],[21,54],[54,103],[103,67],[67,109],[109,10]];
  }

  async initialize({ delegate = "GPU", timeoutMs = 50000 } = {}) {
    this._assertBrowserSupport(delegate);

    const generation = ++this.initializationGeneration;
    this.isClosed = false;
    this.isInitialized = false;
    this.runtimeRecoveryAttempted = false;
    this.runtimeRecoveryPromise = null;
    this.runtimeCloseScheduled = false;

    const creationPromise = this._createLandmarker(delegate, generation);
    creationPromise.then(
      (created) => {
        if (!this._isCurrentGeneration(generation)) {
          safeClose(created.faceLandmarker);
        }
      },
      () => {}
    );

    try {
      const created = await withTimeout(creationPromise, timeoutMs, {
        phase: "initialization",
        delegate,
      });

      if (!this._isCurrentGeneration(generation)) {
        safeClose(created.faceLandmarker);
        throw new FaceLandmarkerError(
          FACE_LANDMARKER_ERROR_CODES.CANCELLED,
          "FaceLandmarker initialization was cancelled",
          { phase: "initialization", delegate }
        );
      }

      this.visionFileset = created.visionFileset;
      this.faceLandmarker = created.faceLandmarker;
      this.delegate = delegate;
      this.isInitialized = true;
      this.lastTimestamp = -Infinity;
      this._notifyDelegateChange(delegate);
      return this;
    } catch (error) {
      const normalized = classifyFaceLandmarkerError(error, {
        phase: "initialization",
        delegate,
      });

      if (normalized.code === FACE_LANDMARKER_ERROR_CODES.INIT_TIMEOUT) {
        this._invalidateGeneration(generation);
      }
      throw normalized;
    }
  }

  setOptions(options) {
    this.options = { ...this.options, ...options };
  }

  onResults(callback) {
    const callbackGeneration = ++this.resultsCallbackGeneration;
    this.onResultsCallback = callback;
    return () => {
      if (
        this.resultsCallbackGeneration === callbackGeneration &&
        this.onResultsCallback === callback
      ) {
        this.onResultsCallback = null;
        this.resultsCallbackGeneration += 1;
      }
    };
  }

  async send(input) {
    const resultsCallback = this.onResultsCallback;
    const resultsCallbackGeneration = this.resultsCallbackGeneration;

    if (!this.isInitialized || !this.faceLandmarker) {
      throw new FaceLandmarkerError(
        FACE_LANDMARKER_ERROR_CODES.RUNTIME_FAILED,
        "FaceLandmarker is not initialized",
        { phase: "runtime", delegate: this.delegate }
      );
    }

    let results;
    try {
      results = this._detect(input.image);
    } catch (error) {
      if (
        this.delegate === "GPU" &&
        (!this.runtimeRecoveryAttempted || this.runtimeRecoveryPromise)
      ) {
        results = await this._recoverOnCpuAndDetect(input.image, error);
      } else {
        const runtimeError = classifyFaceLandmarkerError(error, {
          phase: "runtime",
          delegate: this.delegate,
        });
        this._notifyRuntimeError(runtimeError);
        this._scheduleCloseAfterRuntimeError();
        throw runtimeError;
      }
    }

    const compatibleResults = this.convertToLegacyFormat(results, input.image);
    if (
      resultsCallback &&
      this.onResultsCallback === resultsCallback &&
      this.resultsCallbackGeneration === resultsCallbackGeneration
    ) {
      resultsCallback(compatibleResults);
    }
    return compatibleResults;
  }

  convertToLegacyFormat(modernResults, image) {
    const multiFaceLandmarks = [];
    if (
      modernResults &&
      modernResults.faceLandmarks &&
      modernResults.faceLandmarks.length > 0
    ) {
      multiFaceLandmarks.push(modernResults.faceLandmarks[0]);
    }

    return {
      multiFaceLandmarks,
      image,
    };
  }

  close() {
    this.isClosed = true;
    this.initializationGeneration += 1;
    safeClose(this.faceLandmarker);
    this.faceLandmarker = null;
    this.visionFileset = null;
    this.isInitialized = false;
    this.delegate = null;
    this.onResultsCallback = null;
    this.resultsCallbackGeneration += 1;
    this.lastTimestamp = -Infinity;
    this.runtimeRecoveryPromise = null;
  }

  async _createLandmarker(delegate, generation) {
    const wasmUrl = new URL("wasm/", this.assetBaseUrl).href;
    const modelUrl = new URL(
      "models/face_landmarker.task",
      this.assetBaseUrl
    ).href;

    let visionFileset;
    let modelAssetBuffer;
    try {
      // MediaPipe normally starts fetching the model only after the WASM
      // runtime is ready. Loading both assets in parallel removes that serial
      // network wait on a cold page load.
      [visionFileset, modelAssetBuffer] = await Promise.all([
        FilesetResolver.forVisionTasks(wasmUrl),
        this._loadModelAssetBuffer(modelUrl),
      ]);
    } catch (error) {
      throw classifyFaceLandmarkerError(error, {
        phase: "assets",
        delegate,
      });
    }

    if (!this._isCurrentGeneration(generation)) {
      throw new FaceLandmarkerError(
        FACE_LANDMARKER_ERROR_CODES.CANCELLED,
        "FaceLandmarker initialization was cancelled",
        { phase: "initialization", delegate }
      );
    }

    try {
      const faceLandmarker = await FaceLandmarker.createFromOptions(
        visionFileset,
        this._landmarkerOptions(delegate, modelUrl, modelAssetBuffer)
      );
      return { faceLandmarker, visionFileset };
    } catch (error) {
      throw classifyFaceLandmarkerError(error, {
        phase: "initialization",
        delegate,
      });
    }
  }

  _loadModelAssetBuffer(modelUrl) {
    if (!this.fetchImpl) return Promise.resolve(null);
    if (
      this.modelAssetUrl === modelUrl &&
      this.modelAssetBufferPromise
    ) {
      return this.modelAssetBufferPromise;
    }

    this.modelAssetUrl = modelUrl;
    this.modelAssetBufferPromise = Promise.resolve()
      .then(() => this.fetchImpl(modelUrl))
      .then(response => {
        if (!response || !response.ok) {
          const status = response && Number.isFinite(response.status)
            ? response.status
            : "unknown";
          throw new Error(`FaceLandmarker model request failed (${status})`);
        }
        return response.arrayBuffer();
      })
      .then(buffer => {
        if (!buffer || buffer.byteLength === 0) {
          throw new Error("FaceLandmarker model response was empty");
        }
        return new Uint8Array(buffer);
      })
      .catch(error => {
        this.modelAssetUrl = null;
        this.modelAssetBufferPromise = null;
        throw error;
      });

    return this.modelAssetBufferPromise;
  }

  _landmarkerOptions(delegate, modelAssetPath, modelAssetBuffer = null) {
    const modelOptions = modelAssetBuffer
      ? { modelAssetBuffer: modelAssetBuffer.slice() }
      : { modelAssetPath };
    return {
      baseOptions: {
        ...modelOptions,
        delegate,
      },
      outputFaceBlendshapes: false,
      outputFacialTransformationMatrixes: false,
      runningMode: "VIDEO",
      numFaces: this.options.maxNumFaces,
      minFaceDetectionConfidence: this.options.minDetectionConfidence,
      minFacePresenceConfidence: this.options.minTrackingConfidence,
      minTrackingConfidence: this.options.minTrackingConfidence,
    };
  }

  _detect(image) {
    return this.faceLandmarker.detectForVideo(image, this._nextTimestamp());
  }

  _nextTimestamp() {
    const current = performance.now();
    const next = Math.max(current, this.lastTimestamp + TIMESTAMP_INCREMENT_MS);
    this.lastTimestamp = next;
    return next;
  }

  async _recoverOnCpuAndDetect(image, gpuError) {
    this.runtimeRecoveryAttempted = true;

    if (!this.runtimeRecoveryPromise) {
      this._notifyRuntimeRecoveryState("recovering");
      this.runtimeRecoveryPromise = this._createCpuRecovery().catch((error) => {
        const runtimeError = new FaceLandmarkerError(
          FACE_LANDMARKER_ERROR_CODES.RUNTIME_FAILED,
          "FaceLandmarker GPU inference and CPU recovery both failed",
          {
            phase: "runtime",
            delegate: "CPU",
            originalError: error || gpuError,
          }
        );
        this._notifyRuntimeRecoveryState("failed");
        this._notifyRuntimeError(runtimeError);
        this._scheduleCloseAfterRuntimeError();
        throw runtimeError;
      });
    }

    await this.runtimeRecoveryPromise;

    try {
      return this._detect(image);
    } catch (error) {
      const runtimeError = classifyFaceLandmarkerError(error, {
        phase: "runtime",
        delegate: "CPU",
      });
      this._notifyRuntimeRecoveryState("failed");
      this._notifyRuntimeError(runtimeError);
      this._scheduleCloseAfterRuntimeError();
      throw runtimeError;
    }
  }

  async _createCpuRecovery() {
    const generation = this.initializationGeneration;
    const modelUrl = new URL(
      "models/face_landmarker.task",
      this.assetBaseUrl
    ).href;
    const [visionFileset, modelAssetBuffer] = await Promise.all([
      this.visionFileset
        ? Promise.resolve(this.visionFileset)
        : FilesetResolver.forVisionTasks(
          new URL("wasm/", this.assetBaseUrl).href
        ),
      this._loadModelAssetBuffer(modelUrl),
    ]);

    const creationPromise = FaceLandmarker.createFromOptions(
      visionFileset,
      this._landmarkerOptions("CPU", modelUrl, modelAssetBuffer)
    );
    creationPromise.then(
      (instance) => {
        if (!this._isCurrentGeneration(generation)) safeClose(instance);
      },
      () => {}
    );

    let cpuLandmarker;
    try {
      cpuLandmarker = await withTimeout(
        creationPromise,
        this.runtimeRecoveryTimeoutMs,
        { phase: "runtime", delegate: "CPU" }
      );
    } catch (error) {
      const normalized = classifyFaceLandmarkerError(error, {
        phase: "runtime",
        delegate: "CPU",
      });
      if (normalized.code === FACE_LANDMARKER_ERROR_CODES.INIT_TIMEOUT) {
        // Invalidate immediately so a createFromOptions call that ignores our
        // timeout cannot later become the active CPU instance.
        this._invalidateGeneration(generation);
      }
      throw normalized;
    }

    if (!this._isCurrentGeneration(generation)) {
      safeClose(cpuLandmarker);
      throw new FaceLandmarkerError(
        FACE_LANDMARKER_ERROR_CODES.CANCELLED,
        "FaceLandmarker CPU recovery was cancelled",
        { phase: "runtime", delegate: "CPU" }
      );
    }

    const previousLandmarker = this.faceLandmarker;
    this.faceLandmarker = cpuLandmarker;
    this.visionFileset = visionFileset;
    this.delegate = "CPU";
    this.lastTimestamp = -Infinity;
    safeClose(previousLandmarker);
    this._notifyDelegateChange("CPU");
    this._notifyRuntimeRecoveryState("recovered");
  }

  _assertBrowserSupport(delegate) {
    if (
      typeof WebAssembly === "undefined" ||
      typeof performance === "undefined" ||
      typeof performance.now !== "function" ||
      typeof URL === "undefined"
    ) {
      throw new FaceLandmarkerError(
        FACE_LANDMARKER_ERROR_CODES.UNSUPPORTED_BROWSER,
        "This browser does not support the required FaceLandmarker features",
        { phase: "initialization", delegate }
      );
    }
  }

  _isCurrentGeneration(generation) {
    return !this.isClosed && generation === this.initializationGeneration;
  }

  _invalidateGeneration(generation) {
    if (generation !== this.initializationGeneration) return;

    this.initializationGeneration += 1;
    this.isInitialized = false;
  }

  _notifyDelegateChange(delegate) {
    if (this.onDelegateChange) this.onDelegateChange(delegate);
  }

  _notifyRuntimeError(error) {
    if (this.onRuntimeError) this.onRuntimeError(error);
  }

  _notifyRuntimeRecoveryState(status) {
    if (this.onRuntimeRecoveryState) this.onRuntimeRecoveryState(status);
  }

  _scheduleCloseAfterRuntimeError() {
    if (this.runtimeCloseScheduled) return;
    this.runtimeCloseScheduled = true;

    // send() may still be unwinding through MediaPipe frames. Close in a
    // microtask so standalone adapters are cleaned up without closing a graph
    // synchronously from inside its inference callback.
    Promise.resolve().then(() => {
      if (!this.isClosed) this.close();
    });
  }
}

export default ModernFaceMesh;
